import type {
  FeeModel,
  IAdministrationConfigDto,
  IAdministrationFee,
  IAdministrationFeeTierDto,
  IFeeConfig,
  IFeeResult,
  IFeeTier,
} from './fee-settings.types';

export const round2 = (n: number): number =>
  Math.round((n + Number.EPSILON) * 100) / 100;

/** Formats a number as Dutch euro, e.g. 260 -> "€ 260,00". */
export const formatEuro = (n: number): string =>
  new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
  }).format(Number.isFinite(n) ? n : 0);

/** Formats a percentage with a comma decimal, e.g. 1.3 -> "1,30%". */
export const formatPct = (n: number): string =>
  `${round2(n).toFixed(2).replace('.', ',')}%`;

/** The bracket a basis (excl. VAT) falls into; falls back to the last tier. */
export const tierForBasis = (
  basisExcl: number,
  tiers: IFeeTier[]
): IFeeTier | undefined =>
  tiers.find(t => basisExcl >= t.from && (t.to == null || basisExcl <= t.to)) ??
  tiers[tiers.length - 1];

/**
 * Fee over a booking amount (incl. VAT). m1/m2 are tiered, m3 is flat.
 * Basis = amount excl. VAT; the fee is clamped to the tier's min/max (excl. VAT).
 */
export const calcFee = (
  amountIncl: number,
  cfg: IFeeConfig,
  model: FeeModel | '' = cfg.model
): IFeeResult => {
  const empty: IFeeResult = {
    incl: 0,
    excl: 0,
    vat: 0,
    applied: false,
    clamp: '',
    base: 0,
    tier: null,
    lim: null,
  };
  if (!cfg.feeOn || amountIncl <= 0) return empty;

  const baseExcl = amountIncl / (1 + cfg.vat / 100);
  let excl: number;
  let clamp: '' | 'min' | 'max' = '';
  let tier: IFeeTier | null = null;
  let lim: IFeeResult['lim'];

  if (model === 'm3') {
    lim = cfg.m3;
    excl = (baseExcl * cfg.m3.pct) / 100;
  } else {
    tier = tierForBasis(baseExcl, cfg.tiers) ?? null;
    lim = tier;
    excl = tier ? (baseExcl * tier.pct) / 100 : 0;
  }

  // Round first, then clamp — so the min/max check reflects the value actually
  // shown (e.g. 0.98839 rounds to 0.99 = the min, so it's NOT "raised").
  excl = round2(excl);
  if (lim) {
    if (excl < lim.min) {
      excl = lim.min;
      clamp = 'min';
    } else if (excl > lim.max) {
      excl = lim.max;
      clamp = 'max';
    }
  }

  const incl = round2(excl * (1 + cfg.vat / 100));
  return {
    incl,
    excl,
    vat: round2(incl - excl),
    applied: true,
    clamp,
    base: round2(baseExcl),
    tier,
    lim,
  };
};

/** Ascending, contiguous, no gaps/overlaps; only the last tier may be open-ended. */
export const validateTiers = (tiers: IFeeTier[]): string | null => {
  for (let i = 0; i < tiers.length; i++) {
    const a = tiers[i];
    if (a.to != null && a.to <= a.from)
      return `Tier ${i + 1}: “To” must be greater than “From”.`;
    if (a.max < a.min) return `Tier ${i + 1}: fee max is below fee min.`;
    if (i > 0) {
      const prev = tiers[i - 1];
      if (prev.to == null)
        return `Tier ${i}: only the last tier can have an empty “To”.`;
      if (Math.abs(a.from - (prev.to + 0.01)) > 0.011)
        return `Tier ${i + 1}: must start at ${formatEuro(
          round2(prev.to + 0.01)
        )} — no gaps or overlaps.`;
    }
  }
  return null;
};

/** Soft check: the fee must never drop when crossing a boundary (next min >= prev max). */
export const tierDropWarning = (tiers: IFeeTier[]): string | null => {
  for (let i = 1; i < tiers.length; i++) {
    if (tiers[i].min < tiers[i - 1].max - 0.001) {
      return `Note: the fee drops at the ${formatEuro(
        tiers[i].from
      )} boundary (min ${formatEuro(tiers[i].min)} < previous max ${formatEuro(
        tiers[i - 1].max
      )}). Rule of thumb: Fee min = previous Fee max.`;
    }
  }
  return null;
};

/** Appends a new open-ended tier after the current last one. */
export const appendTier = (tiers: IFeeTier[]): IFeeTier[] => {
  const next = tiers.map(t => ({ ...t }));
  const last = next[next.length - 1];
  let from = 0;
  if (last) {
    if (last.to == null) last.to = round2(last.from + 299.99);
    from = round2(last.to + 0.01);
  }
  next.push({
    from,
    to: null,
    pct: last ? last.pct : 1,
    min: last ? last.min : 0.99,
    max: last ? last.max : 4.99,
  });
  return next;
};

/** Maps an editor tier to the API tier DTO. */
export const tierToDto = (t: IFeeTier): IAdministrationFeeTierDto => ({
  fromAmount: t.from,
  toAmount: t.to,
  percentage: t.pct,
  minFee: t.min,
  maxFee: t.max,
});

/** Maps an API tier record to an editor tier. */
export const recordToTier = (r: IAdministrationFee): IFeeTier => ({
  from: r.fromAmount ?? 0,
  to: r.toAmount ?? null,
  pct: r.percentage ?? 0,
  min: r.minFee ?? 0,
  max: r.maxFee ?? 0,
});

/** Maps the API model identifier (e.g. "model_1") to the editor's FeeModel; '' when unset. */
export const apiModelToUi = (model?: string | null): FeeModel | '' => {
  switch (model) {
    case 'model_1':
      return 'm1';
    case 'model_2':
      return 'm2';
    case 'model_3':
      return 'm3';
    default:
      return '';
  }
};

/** Maps the editor's FeeModel to the API model identifier (e.g. "model_1"). */
export const uiModelToApi = (model: FeeModel): string => {
  switch (model) {
    case 'm2':
      return 'model_2';
    case 'm3':
      return 'model_3';
    default:
      return 'model_1';
  }
};

/** Merges the API config into an editor config, keeping local-only fields (VAT, m3). */
export const configToEditor = (
  dto: IAdministrationConfigDto,
  base: IFeeConfig
): IFeeConfig => ({
  ...base,
  model: apiModelToUi(dto.model),
  tiers: (dto.tiers ?? []).map(recordToTier).sort((a, b) => a.from - b.from),
  // The API returns monthlyThreshold as a string (e.g. "49.00") — coerce to number.
  thr: dto.monthlyThreshold != null ? Number(dto.monthlyThreshold) : base.thr,
  thrOn: dto.monthlyThresholdActive ?? base.thrOn,
  feeOn: dto.administrationFeeActive ?? base.feeOn,
  giftCardFeeOn: dto.giftCardFeeActive ?? base.giftCardFeeOn,
});

/**
 * Builds the PUT /administration/config body from the editor config.
 * `companyId` is null for the global/default config (Fee Settings page).
 */
export const configToDto = (
  cfg: IFeeConfig,
  companyId: string | null = null
): IAdministrationConfigDto => ({
  companyId,
  model: cfg.model ? uiModelToApi(cfg.model) : undefined,
  // A fixed-fee tier (min === max) has no meaningful percentage — send 0.
  tiers: cfg.tiers.map(t => tierToDto(t.min === t.max ? { ...t, pct: 0 } : t)),
  monthlyThreshold: Number.isNaN(Number(cfg.thr)) ? undefined : Number(cfg.thr),
  monthlyThresholdActive: cfg.thrOn,
  administrationFeeActive: cfg.feeOn,
  giftCardFeeActive: cfg.giftCardFeeOn,
});
